import React, { useState, useEffect } from "react"
import {Tag, Plus, Edit2, Trash2, X, Save, Eye} from 'lucide-react'
import { lumi } from "../lib/lumi"
import { useNavigate } from "react-router-dom"

interface CustomerTag {
  _id: string
  name: string
  color: string
  usageCount: number
  createdAt: string
  updatedAt: string
}

const CustomerTagManager: React.FC = () => {
  const navigate = useNavigate()
  const [tags, setTags] = useState<CustomerTag[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingTag, setEditingTag] = useState<CustomerTag | null>(null)
  const [formData, setFormData] = useState({
    name: ""
  })

  // 加载标签列表
  const loadTags = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.customer_tags.list({
        sort: { createdAt: -1 }
      })
      setTags(response?.list || [])
    } catch (error) {
      console.error("加载标签失败:", error)
      alert("加载标签失败")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTags()
  }, [])

  // 打开新增标签 Modal
  const handleAddNew = () => {
    setEditingTag(null)
    setFormData({
      name: ""
    })
    setShowModal(true)
  }

  // 打开编辑标签 Modal
  const handleEdit = (tag: CustomerTag) => {
    setEditingTag(tag)
    setFormData({
      name: tag.name
    })
    setShowModal(true)
  }

  // 保存标签（新增或编辑）
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("请输入标签名称")
      return
    }

    try {
      const now = new Date().toISOString()

      if (editingTag) {
        const oldName = editingTag.name
        const newName = formData.name.trim()
        
        // 1. 更新标签表
        await lumi.entities.customer_tags.update(editingTag._id, {
          name: newName,
          updatedAt: now
        })
        
        // 2. 同步更新所有会员记录中的标签名称
        if (oldName !== newName && editingTag.usageCount > 0) {
          const customersResponse = await lumi.entities.customers.list({})
          const customers = customersResponse?.list || []
          
          for (const customer of customers) {
            if (customer.tags && Array.isArray(customer.tags) && customer.tags.includes(oldName)) {
              const updatedTags = customer.tags.map((t: string) => t === oldName ? newName : t)
              await lumi.entities.customers.update(customer._id, {
                tags: updatedTags
              })
            }
          }
        }
        
        alert("标签更新成功，已同步更新所有会员记录")
      } else {
        // 新增标签（使用默认灰色）
        await lumi.entities.customer_tags.create({
          name: formData.name.trim(),
          color: "#6b7280",
          usageCount: 0,
          creator: lumi.auth.user?.email || "system",
          createdAt: now,
          updatedAt: now
        })
        alert("标签创建成功")
      }

      setShowModal(false)
      loadTags()
    } catch (error) {
      console.error("保存标签失败:", error)
      alert("保存标签失败")
    }
  }

  // 删除标签
  const handleDelete = async (tag: CustomerTag) => {
    if (tag.usageCount > 0) {
      if (!confirm(`此标签正被 ${tag.usageCount} 位会员使用，确定要删除吗？删除后会员的标签也会被移除。`)) {
        return
      }
    } else {
      if (!confirm(`确定要删除标签"${tag.name}"吗？`)) {
        return
      }
    }

    try {
      await lumi.entities.customer_tags.delete(tag._id)
      
      // 如果有会员使用此标签，需要从会员记录中移除
      if (tag.usageCount > 0) {
        const customersResponse = await lumi.entities.customers.list({})
        const customers = customersResponse?.list || []
        
        for (const customer of customers) {
          if (customer.tags && Array.isArray(customer.tags) && customer.tags.includes(tag.name)) {
            const updatedTags = customer.tags.filter((t: string) => t !== tag.name)
            await lumi.entities.customers.update(customer._id, {
              tags: updatedTags
            })
          }
        }
      }

      alert("标签删除成功")
      loadTags()
    } catch (error) {
      console.error("删除标签失败:", error)
      alert("删除标签失败")
    }
  }

  // 关闭 Modal
  const handleCloseModal = () => {
    setShowModal(false)
    setEditingTag(null)
  }

  // 查看使用此标签的会员
  const viewTagCustomers = (tagName: string) => {
    navigate(`/admin/customers?tag=${encodeURIComponent(tagName)}`)
  }

  if (loading) {
    return (
      <div className="p-8 h-full flex items-center justify-center">
        <div className="text-gray-400">载入中...</div>
      </div>
    )
  }

  return (
    <div className="p-8 h-full flex flex-col overflow-hidden">
      {/* 页面标题 */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-light tracking-wider mb-2">会员标签管理</h2>
          <p className="text-gray-400">管理会员标签和查看使用统计</p>
        </div>
        <button
          onClick={handleAddNew}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          新增标签
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {tags.length}
          </div>
          <div className="text-sm text-gray-400">总标签数</div>
        </div>

        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {tags.reduce((sum, tag) => sum + tag.usageCount, 0)}
          </div>
          <div className="text-sm text-gray-400">总使用次数</div>
        </div>

        <div className="bg-gray-950 border border-gray-800 p-6">
          <div className="text-3xl font-light text-gray-100 mb-2">
            {tags.filter(tag => tag.usageCount > 0).length}
          </div>
          <div className="text-sm text-gray-400">已使用标签</div>
        </div>
      </div>

      {/* 标签列表 */}
      <div className="flex-1 overflow-y-auto bg-gray-950 border border-gray-800">
        {tags.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Tag size={48} className="mx-auto mb-4 opacity-50" />
            <p>暂无标签</p>
            <p className="text-sm mt-2">点击"新增标签"创建第一个标签</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 sticky top-0">
                <tr className="border-b border-gray-800">
                  <th className="text-left px-6 py-4 text-gray-400 font-light tracking-wide">标签名称</th>
                  <th className="text-left px-6 py-4 text-gray-400 font-light tracking-wide">显示预览</th>
                  <th className="text-left px-6 py-4 text-gray-400 font-light tracking-wide">使用次数</th>
                  <th className="text-left px-6 py-4 text-gray-400 font-light tracking-wide">创建时间</th>
                  <th className="text-right px-6 py-4 text-gray-400 font-light tracking-wide">操作</th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <tr key={tag._id} className="border-b border-gray-800 hover:bg-gray-900 transition-colors">
                    <td className="px-6 py-4 text-gray-300">{tag.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-gray-800 text-gray-300 text-sm">
                        {tag.name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {tag.usageCount} 位会员
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(tag.createdAt).toLocaleDateString("zh-CN")}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewTagCustomers(tag.name)}
                          className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-800 rounded transition-colors"
                          title="查看使用此標籤的會員"
                          disabled={tag.usageCount === 0}
                        >
                          <Eye size={18} className={tag.usageCount === 0 ? "opacity-30" : ""} />
                        </button>
                        <button
                          onClick={() => handleEdit(tag)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded transition-colors"
                          title="编辑"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(tag)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-800 rounded transition-colors"
                          title="删除"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 新增/编辑标签 Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-950 border border-gray-800 w-full max-w-lg">
            {/* Modal 标题 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h3 className="text-xl font-light tracking-wider">
                {editingTag ? "编辑标签" : "新增标签"}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal 内容 */}
            <div className="p-6">
              {/* 标签名称 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  标签名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如：VIP會員、企業客戶"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 text-gray-100 focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Modal 底部按钮 */}
            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-3 border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Save size={18} />
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerTagManager
